'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ROICalculator() {
  const [quotesPerWeek, setQuotesPerWeek] = useState(20);
  const [timePerQuote, setTimePerQuote] = useState(45);

  const calculateSavings = () => {
    const currentTimePerWeek = (quotesPerWeek * timePerQuote) / 60; // hours
    const newTimePerWeek = (quotesPerWeek * 3) / 60; // 3 min per quote
    const timeSaved = currentTimePerWeek - newTimePerWeek;
    const hourlyCost = 75; // Average contractor hourly rate
    const weeklySavings = timeSaved * hourlyCost;
    const monthlySavings = weeklySavings * 4;
    const yearlySavings = monthlySavings * 12;

    return {
      timeSaved: Math.round(timeSaved * 10) / 10,
      weeklySavings: Math.round(weeklySavings),
      monthlySavings: Math.round(monthlySavings),
      yearlySavings: Math.round(yearlySavings),
    };
  };

  const savings = calculateSavings();

  return (
    <section className="section-padding bg-white border-t border-border">
      <div className="container-custom">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Calculate Your <span className="text-success">ROI</span>
          </h2>
          <p className="text-xl text-muted-foreground">
            See how much time and money you could save with HVAC Quotes
          </p>
        </div>

        <div className="max-w-4xl mx-auto bg-gradient-to-br from-muted/50 to-background border border-border rounded-2xl p-8 md:p-12 shadow-xl">
          <div className="grid md:grid-cols-2 gap-12 mb-12">
            {/* Input Controls */}
            <div className="space-y-8">
              <div>
                <label className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-foreground uppercase tracking-wider">
                    Quotes per Week
                  </span>
                  <span className="text-2xl font-mono-numbers font-bold text-primary">
                    {quotesPerWeek}
                  </span>
                </label>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={quotesPerWeek}
                  onChange={(e) => setQuotesPerWeek(parseInt(e?.target?.value))}
                  className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>5</span>
                  <span>100</span>
                </div>
              </div>

              <div>
                <label className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold text-foreground uppercase tracking-wider">
                    Current Time per Quote (min)
                  </span>
                  <span className="text-2xl font-mono-numbers font-bold text-primary">
                    {timePerQuote}
                  </span>
                </label>
                <input
                  type="range"
                  min="15"
                  max="120"
                  step="5"
                  value={timePerQuote}
                  onChange={(e) => setTimePerQuote(parseInt(e?.target?.value))}
                  className="w-full h-2 bg-border rounded-full appearance-none cursor-pointer accent-primary"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>15 min</span>
                  <span>120 min</span>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="bg-white rounded-xl border border-border p-8 shadow-lg">
              <div className="flex items-center gap-2 text-success mb-6">
                <Icon name="ChartBarIcon" size={24} variant="solid" />
                <h3 className="text-sm uppercase tracking-widest font-semibold">
                  Your Savings
                </h3>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    Time Saved per Week
                  </div>
                  <div className="text-3xl font-mono-numbers font-bold text-foreground">
                    {savings?.timeSaved} hours
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground mb-1">
                    Monthly Savings
                  </div>
                  <div className="text-3xl font-mono-numbers font-bold text-success">
                    ${savings?.monthlySavings?.toLocaleString()}
                  </div>
                </div>

                <div className="pt-6 border-t border-border">
                  <div className="text-sm text-muted-foreground mb-1">
                    Yearly Savings
                  </div>
                  <div className="text-4xl font-mono-numbers font-bold text-success">
                    ${savings?.yearlySavings?.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center pt-8 border-t border-border">
            <p className="text-muted-foreground mb-6">
              Based on $75/hour contractor rate and 3-minute quote time with HVAC
              Quotes
            </p>
            <button className="px-8 py-3 bg-primary text-primary-foreground rounded-full font-semibold uppercase tracking-wide hover:bg-primary/90 transition-all hover:scale-105 shadow-lg">
              Start Saving Today
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}